import React from 'react';
import { Div } from '@dnb/eufemia';
import { isEmpty } from 'lodash';
import { getChildren } from '../../helpers/extractChildren';
import { InnerComponent } from '../types';
import Content from './Content/Content';
import DrawerPageLayout from './DrawerPageLayout/DrawerPageLayout';
import Header from './Header/Header';
import OverlappingHeaderContent from './OverlappingHeaderContent/OverlappingHeaderContent';
import styles from './PageLayout.module.scss';

interface Props {
  children: React.ReactNode;
  narrow?: boolean;
  onDrawerClose?: () => void;
}

const PageLayout = ({ children, narrow = false, onDrawerClose }: Props) => {
  const childrenArray = React.Children.toArray(children);

  if (onDrawerClose) {
    return (
      <DrawerPageLayout
        onClose={onDrawerClose}
        breadcrumb={getBreadcrumb()}
        header={getHeader()}
        content={getContent()}
      />
    );
  }

  return (
    <Div>
      <Div className={styles.header}>
        {getBreadcrumb()}
        <Header narrow={narrow}>{getHeader()}</Header>
      </Div>
      {getOverlappingHeaderContent()}
      <Content narrow={narrow}>{getContent()}</Content>
      {getContentFooter()}
    </Div>
  );

  function getHeader() {
    const header = getPageElement(PageLayout.Header);

    if (isEmpty(header)) throw Error(getMissingChildErrorMessage('Header'));

    return header;
  }

  function getBreadcrumb() {
    const breadcrumb = getPageElement(PageLayout.Breadcrumb);

    if (isEmpty(breadcrumb)) return null;
    return (
      <Div bottom="x-small" data-testid="breadcrumb">
        {breadcrumb}
      </Div>
    );
  }

  function getOverlappingHeaderContent() {
    const overlappingHeaderContent = getPageElement(
      PageLayout.OverlappingHeaderContent
    );

    if (isEmpty(overlappingHeaderContent)) return null;
    return (
      <OverlappingHeaderContent narrow={narrow}>
        {overlappingHeaderContent}
      </OverlappingHeaderContent>
    );
  }

  function getContent() {
    const content = getPageElement(PageLayout.Content);

    if (isEmpty(content)) throw Error(getMissingChildErrorMessage('Content'));

    return content;
  }

  function getContentFooter() {
    const contentFooter = getPageElement(PageLayout.ContentFooter);

    if (isEmpty(contentFooter)) return null;

    return (
      <Div data-testid="content-footer" className={styles.footer}>
        {contentFooter}
      </Div>
    );
  }

  function getPageElement(
    element: ({ children }: InnerComponent) => React.ReactChild
  ) {
    const children = getChildren(childrenArray, element);

    if (isEmpty(children)) return null;
    return children;
  }
};

PageLayout.Breadcrumb = ({ children }: InnerComponent) => {
  return <React.Fragment key="breadcrumb">{children}</React.Fragment>;
};
PageLayout.Header = ({ children }: InnerComponent) => {
  return <React.Fragment key="header">{children}</React.Fragment>;
};
PageLayout.OverlappingHeaderContent = ({ children }: InnerComponent) => {
  return (
    <React.Fragment key="overlapping-header-content">{children}</React.Fragment>
  );
};
PageLayout.Content = ({ children }: InnerComponent) => {
  return <React.Fragment key="content">{children}</React.Fragment>;
};
PageLayout.ContentFooter = ({ children }: InnerComponent) => {
  return <React.Fragment key="content-footer">{children}</React.Fragment>;
};

function getMissingChildErrorMessage(missingComponent: string) {
  return `PageLayout: ${missingComponent} is required. Please add <PageLayout.${missingComponent}>`;
}

export default PageLayout;
